import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Tabs from "antd/es/tabs";
import { defineMessages, useIntl } from "react-intl";

import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  EntityTypeSupportingGroups,
  Group,
} from "@mapmycustomers/shared/types/entity";
import { Modal } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { enterGroupMode } from "@app/scene/map/store/groupMode/actions";
import { fetchAllGroups } from "@app/store/groups/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import styles from "./GroupsModal.module.scss";
import GroupsTab from "./GroupsTab";

const messages = defineMessages({
  [EntityType.COMPANY]: {
    id: "map.toolbar.groups.modal.tabs.companies",
    defaultMessage: "Companies",
    description: "Companies tab title in Groups Popover on Map page",
  },
  [EntityType.DEAL]: {
    id: "map.toolbar.groups.modal.tabs.deals",
    defaultMessage: "Deals",
    description: "Deals tab title in Groups Popover on Map page",
  },
  [EntityType.PERSON]: {
    id: "map.toolbar.groups.modal.tabs.people",
    defaultMessage: "People",
    description: "People tab title in Groups Popover on Map page",
  },
  groups: {
    id: "map.toolbar.groups.modal",
    defaultMessage: "Groups",
    description: "Title of the Groups popover",
  },
});

type GroupsModalProps = {
  onHide?: () => void;
  open: boolean;
} & typeof mapDispatchToProps;

const GroupsModal: React.FC<GroupsModalProps> = ({
  onEnterGroupMode,
  onFetchAllGroups,
  onHide,
  open,
}) => {
  const analytics = useExtendedAnalytics("Groups Popover", useAnalytics());
  const intl = useIntl();

  const handleClick = useCallback(
    (group: Group, entityType: EntityTypeSupportingGroups) => {
      onEnterGroupMode({ entityType, group, groupId: group.id });
      onHide?.();
      analytics.clicked(["View Group"], {
        entityType: getEntityTypeAnalyticsName(entityType, false, false),
        groupId: group.id,
      });
    },

    [analytics, onEnterGroupMode, onHide]
  );

  const [activeEntity, setActiveEntity] = useState<EntityTypesSupportedByMapsPage>(
    EntityType.COMPANY
  );
  const handleTabClick = useCallback(
    (key: string) => {
      setActiveEntity(key as EntityTypesSupportedByMapsPage);
      analytics.clicked([
        "Tab",
        getEntityTypeAnalyticsName(key as EntityTypesSupportedByMapsPage, false, true),
      ]);
    },
    [analytics]
  );

  const items = useMemo(
    () =>
      MAP_ENTITY_TYPES.map((entityType) => ({
        key: entityType,
        label: intl.formatMessage(messages[entityType]),
      })),
    [intl]
  );

  useEffect(() => {
    if (open) {
      onFetchAllGroups();
    }
  }, [open, onFetchAllGroups]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Modal
        className={styles.modal}
        destroyOnClose
        footer={null}
        onCancel={onHide}
        open={open}
        title={intl.formatMessage(messages.groups)}
        width="750px"
      >
        <ExtendedAnalytics keyPath={getEntityTypeAnalyticsName(activeEntity, false, true)}>
          <Tabs
            activeKey={activeEntity}
            className={styles.tabs}
            items={items}
            onTabClick={handleTabClick}
          />
          <GroupsTab entityType={activeEntity} onClick={handleClick} onHide={onHide} />
        </ExtendedAnalytics>
      </Modal>
    </AnalyticsContext.Provider>
  );
};

const mapDispatchToProps = {
  onEnterGroupMode: enterGroupMode,
  onFetchAllGroups: fetchAllGroups.request,
};

export default connect(null, mapDispatchToProps)(GroupsModal);
